import ReactCodeMirror from '@uiw/react-codemirror';
import { LinearProgress } from '@mui/material';
import { ReportTemplateEditorCard } from './ReportTemplateEditorCard';
import { php } from '@codemirror/lang-php';
import { dracula } from '@uiw/codemirror-theme-dracula';
import useQuery from 'hooks/useQuery';
import { GeneralIds, ModelTypes } from 'types/types';
import { useParams } from 'react-router-dom';
import { FormTemplate, FormTemplateType } from 'api/models';
import {
  getReportTemplateFieldTemplate,
  getReportTemplateLayout,
} from './utils';
import { getFieldAlias } from 'api';
import {
  fieldTemplatesState,
  formTemplateState,
  formTemplateVersionsState,
  layoutSelector,
} from 'state';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { useMemo } from 'react';
import { FormTemplateStatus } from 'api/models/FormTemplate';

export default function ReportTemplateEditor() {
  const { formTemplateTypeId, projectId } = useParams<GeneralIds>();
  const setFormTemplateVersions = useSetRecoilState(formTemplateVersionsState);
  const setLayout = useSetRecoilState(layoutSelector);
  const [fieldTemplates, setFieldTemplates] =
    useRecoilState(fieldTemplatesState);
  const [formTemplate, setFormTemplate] = useRecoilState(formTemplateState);

  const { data: reportTemplateType } = useQuery<FormTemplateType>({
    type: ModelTypes.formTemplateType,
    ids: {
      id: formTemplateTypeId,
      projectId,
    },
  });

  const isReadonly = useMemo(() => formTemplate?.status === FormTemplateStatus.Published, [formTemplate?.status]);

  const { loading } = useQuery<FormTemplate[]>({
    type: ModelTypes.formTemplate,
    ids: {
      projectId,
      formTemplateTypeId,
    },
    set: {
      data: async data => {
        setFormTemplateVersions([...data]);
        const formTemplate = data[data.length - 1];
        if (formTemplate) {
          setLayout({
            children: formTemplate.layout.items ?? [],
          });
          setFieldTemplates(formTemplate.fieldTemplates ?? []);

          setFormTemplate(formTemplate);
        } else {
          setFormTemplate(undefined);

          const alias = await getFieldAlias({
            projectId: projectId,
            title: 'report_code_field',
            usedAliases: [],
          });
          const fieldTemplate = getReportTemplateFieldTemplate(alias);
          setFieldTemplates([fieldTemplate]);
          setLayout({
            children: [getReportTemplateLayout(fieldTemplate.id)],
          });
        }
      },
    },
  });

  const fieldTemplate = fieldTemplates[0];

  return (
    <>
      <ReportTemplateEditorCard reportTemplateType={reportTemplateType} />
      {loading && <LinearProgress />}
      {!loading && (
        <ReactCodeMirror
          readOnly={isReadonly}
          height="50%"
          theme={dracula}
          style={{ borderRadius: 4, marginTop: 16 }}
          value={fieldTemplate?.fieldAttributes?.code}
          onChange={value => {
            setFieldTemplates([
              {
                ...fieldTemplate,
                fieldAttributes: {
                  ...fieldTemplate.fieldAttributes,
                  code: value,
                },
              },
            ]);
          }}
          extensions={[php({ plain: true })]}
        />
      )}
    </>
  );
}
